import type { GoogleGeocodeResult } from './googleMapsLoader';

export interface ProjectCityLocation {
  id: string;
  title: string;
  city: string | null;
  state: string | null;
  riskLevel: 'CRITICAL' | 'HIGH' | 'MODERATE' | 'LOW' | null;
  pinColor?: string;
}

const normalise = (value: string) => value.trim().toLocaleLowerCase('en-US');

export function validProjectCoordinates(latitude: unknown, longitude: unknown): boolean {
  return (
    typeof latitude === 'number' &&
    Number.isFinite(latitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    typeof longitude === 'number' &&
    Number.isFinite(longitude) &&
    longitude >= -180 &&
    longitude <= 180
  );
}

export function withGeocodingTimeout<T>(request: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('Google geocoding timed out')), 15_000);
    void request.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });
}

/** A city centroid is never presented as a verified job-site coordinate. */
export function resolveProjectCity(city: string, state: string, results: GoogleGeocodeResult[]) {
  if (results.length !== 1 || results[0].partial_match) return null;
  const result = results[0];
  const cityPart = result.address_components.find(
    (part) => part.types.includes('locality') || part.types.includes('postal_town'),
  );
  const statePart = result.address_components.find((part) =>
    part.types.includes('administrative_area_level_1'),
  );
  const matches = (part: typeof cityPart, input: string) =>
    Boolean(
      part &&
        [part.long_name, part.short_name].some((value) => normalise(value) === normalise(input)),
    );
  if (!matches(cityPart, city) || !matches(statePart, state)) return null;
  const latitude = result.geometry.location.lat();
  const longitude = result.geometry.location.lng();
  if (
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90 ||
    !Number.isFinite(longitude) ||
    longitude < -180 ||
    longitude > 180
  )
    return null;
  return { latitude, longitude };
}
